import React, { useMemo } from 'react'; // Import useMemo
import { useCart } from '../context/cartContext';
import { CartItem, displayPrice, OrderItem, Customer } from '../utils/types';
import {
    createOrder,
    sendWhatsAppMessage, // Import WhatsApp function
    sendOrderEmail     // Import Email function
} from '../utils/api'; import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';

// --- Import Context Hooks and Auth Utils ---
import { useAuth } from '../context/AuthContext';
import { useCustomerContext } from '../context/customerContext';
import { hasRole } from '../utils/auth'; // Assuming path is correct



// Access API URL for constructing the PDF link
const API_URL = window.APP_CONFIG?.API_URL || process.env.REACT_APP_API_URL;

// --- Cart Component ---
const Cart: React.FC = () => {
    const { state, dispatch } = useCart();
    const { items } = state;
    const navigate = useNavigate();

    // --- Get User and Customer Context ---
    const { user: loggedInUser } = useAuth();
    const { selectedSessionCustomer, customers: customerList } = useCustomerContext(); // Get full customer list too

    // --- Determine Effective User (for checkout) ---
    const effectiveUser = useMemo(() => {
        const isAdminOrStaff = hasRole(['super_admin', 'staff']);
        const isAdminActingAs = isAdminOrStaff && selectedSessionCustomer;

        if (isAdminActingAs) {
            // Admin is acting as another customer
            const customerRole = selectedSessionCustomer?.role || 'dealer'; // Default or fetched role
            return {
                id: selectedSessionCustomer.user_id, // The user ID associated with the customer
                role: customerRole,
                isAuthenticated: true,
                customer_id: selectedSessionCustomer.id, // The actual customer ID
                // Attempt to get name from selectedSessionCustomer or fallback
                name: selectedSessionCustomer?.name || 'Unknown Customer'
            };
        } else {
            // Use the logged-in user's details
            let loggedInUserRole: string | undefined;
            let customerIdForUser: number | undefined;
            try {
                const rolesPermissionsItem = localStorage.getItem('rolesPermissions');
                if (rolesPermissionsItem) {
                    const rolesPermissions = JSON.parse(rolesPermissionsItem);
                    loggedInUserRole = rolesPermissions?.roles?.[0];
                }
                // Attempt to get customer ID from localStorage (might be just the ID)
                const customerInfo = localStorage.getItem('customer');
                if (customerInfo) {
                    // Check if it's just the ID or a full object (adjust parsing if needed)
                    try {
                        const parsedCustomer = JSON.parse(customerInfo);
                        customerIdForUser = typeof parsedCustomer === 'number' ? parsedCustomer : parsedCustomer?.id;
                    } catch {
                        // Handle case where 'customer' in localStorage might not be valid JSON
                        console.warn("Could not parse 'customer' from localStorage in Cart.");
                    }
                }
                // Fallback if not found in localStorage but might be nested in user object
                if (customerIdForUser === undefined) {
                    customerIdForUser = loggedInUser?.customer?.id;
                }

            } catch (e) {
                console.error("Failed to parse rolesPermissions/customer from localStorage in Cart:", e);
            }

            return {
                id: loggedInUser?.id,
                role: loggedInUserRole,
                isAuthenticated: !!loggedInUser,
                customer_id: customerIdForUser, // Use the determined customer ID
                name: loggedInUser?.name || 'Guest'
            };
        }
    }, [loggedInUser, selectedSessionCustomer]);
    // --- End Determine Effective User ---


    const handleRemoveFromCart = (cartItemId: string) => {
        dispatch({
            type: 'REMOVE_FROM_CART',
            payload: { cartItemId },
        });
    };

    const calculateTotal = () => {
        return items.reduce((total: number, item: CartItem) => total + item.price * item.quantity, 0);
    };

    const handleCheckout = async () => {
        // --- Use effectiveUser details ---
        const effectiveCustomerId = effectiveUser.customer_id;
        const effectiveUserId = effectiveUser.id;

        // Find the customer's name from the full list if acting as, otherwise use effectiveUser.name
        let customerNameForOrder = effectiveUser.name;
        if (hasRole(['super_admin', 'staff']) && selectedSessionCustomer) {
            const foundCustomer = customerList.find(c => c.id === effectiveCustomerId);
            customerNameForOrder = foundCustomer?.name || selectedSessionCustomer.name || 'Unknown Customer';
        }


        if (effectiveCustomerId === undefined || effectiveCustomerId === null || effectiveUserId === undefined) {
            toast.error("Cannot checkout: Customer or User information is missing.");
            console.error("Checkout failed: effectiveCustomerId or effectiveUserId is missing.", effectiveUser);
            return;
        }
        // --- End Use effectiveUser details ---


        const orderItems: OrderItem[] = items.map((item: CartItem): OrderItem => {
            // Modifier price calculation seems okay, but ensure item.price is the correct base
            // const modifierTotalPrice = item.modifiers.reduce((sum, modifier) => sum + Number(modifier.product_value), 0);
            // const totalPrice = Number(item.price) + Number(modifierTotalPrice); // This seems incorrect, item.price should already include modifier price if added correctly

            return {
                order_id: 0, // Will be set by backend
                customer_id: effectiveCustomerId, // Use effective customer ID
                category_id: item.product_category, // Use category from cart item
                product_category: item.product_category,
                product_id: item.id,
                product_code: item.product_code,
                product_name: item.product_name,
                quantity: item.quantity,
                product_value: item.product_value, // SRP for reference
                price: item.price, // This should be the final price (base + modifiers) calculated when added to cart
                total_price: (item.price * item.quantity),
                payment_desc: item.product_code,
                payment_for: item.product_name,
                qty: item.quantity,
                unit_price: item.price, // Unit price is the final price per item
                balance: (item.price * item.quantity),
                tranx_details: '', // Or add relevant details
                modifiers: item.modifiers,
                checkout: false, // Assuming this is correct for new order items
            };
        });

        const orderPayload = {
            customerId: effectiveCustomerId, // Use effective customer ID
            orderType: 'order',
            tableId: '1', // Consider if this needs to be dynamic
            items: orderItems,
            status: 'pending',
            amountToPay: calculateTotal(),
            userId: effectiveUserId, // Use effective user ID (could be admin's or customer's user_id)
            table: [], // Assuming empty is correct
            customer_name: customerNameForOrder, // Use the determined customer name
        };

        try {
            // No need to get token here, api instance should handle it
            // const token = localStorage.getItem('token');

            const response = await createOrder(orderPayload);
            console.log('Order created:', response);

            dispatch({ type: 'CLEAR_CART' });

            toast.success('Order created successfully!', {
                position: "top-center",
                autoClose: 1500,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
            });

            // Navigate to the order details page using the ID from the response
            if (response.data?.id) {

                const newOrderId = response.data.id;
                const newOrderUuid = response.data.order_uuid; // Assume this is returned

                // --- Determine Customer Contact Details for Notifications ---
                let targetPhoneNumber: string | undefined | null;
                let recipientEmail: string | undefined | null;
                let customerForNotifications: Customer | undefined | null = null;

                if (hasRole(['super_admin', 'staff']) && selectedSessionCustomer) {
                    customerForNotifications = selectedSessionCustomer;
                } else if (effectiveUser.customer_id) {
                    customerForNotifications = customerList.find(c => c.id === effectiveUser.customer_id);
                }

                targetPhoneNumber = customerForNotifications?.phone;
                recipientEmail = customerForNotifications?.email;

                // --- Send WhatsApp Message ---
                if (targetPhoneNumber && newOrderUuid) {
                    try {
                        const pdfLink = `${API_URL}/orders/${newOrderUuid}/pdf`;
                        const settings = JSON.parse(localStorage.getItem('settings') || '{}');
                        const whatsappMessage = `*${settings.shortname || 'Our Store'}* sent you a document.\n\n\nOrder *${newOrderId}* PDF is ready.\n\nDownload here: ${pdfLink}\n\n\n*Note: This is a bot message, please do not reply.*\n\n*Please contact ${settings.shortname || 'Us'} at ${settings.phone || 'our contact number'} instead.*`;

                        const whatsappMessage2owner = `*${settings.shortname || 'Our Store'}* : New Order *${newOrderId}* PDF is ready.\n\nDownload here: ${pdfLink}\n\n*Note: This is a bot new order ${newOrderId} notification.`;

                        await sendWhatsAppMessage(targetPhoneNumber, whatsappMessage);
                        await sendWhatsAppMessage(settings.phone, whatsappMessage2owner);
                        await sendWhatsAppMessage('+601112163808', whatsappMessage2owner);
                        toast.info(`Order PDF link sent via WhatsApp to ${targetPhoneNumber}.`);
                    } catch (whatsappError: any) {
                        console.error('Failed to send WhatsApp message after order creation:', whatsappError);
                        toast.error(`Failed to send WhatsApp link: ${whatsappError.message || 'Unknown error'}`);
                    }
                } else if (newOrderUuid && !targetPhoneNumber) {
                    toast.warn("Order created, but customer phone number is missing for WhatsApp notification.");
                }

                // --- Send Email ---
                if (recipientEmail) {
                    try {
                        await sendOrderEmail(newOrderId, recipientEmail);
                        toast.success(`Order confirmation email sent to ${recipientEmail}.`);
                    } catch (emailError: any) {
                        console.error('Failed to send order email after order creation:', emailError);
                        toast.error(`Failed to send order email: ${emailError.message || 'Unknown error'}`);
                    }
                } else {
                    toast.warn("Order created, but customer email is missing for email notification.");
                }

                navigate(`/order/${newOrderId}`);
            
            } else {
                console.error("Order created, but no ID received in response.", response);
                navigate('/order-history'); // Fallback navigation
            }

        } catch (error) {
            console.error('Error creating order:', error);
            toast.error('Error creating order! Please try again.');
        }
    };

    // console.log('Effective User in Cart:', effectiveUser);
    // console.log('Cart Items:', items);

    return (
        <div className="container mx-auto p-4">
            <h1 className="text-2xl font-bold mb-4">Cart</h1>
            {items.length === 0 ? (
                <p className="text-gray-600">Your cart is empty</p>
            ) : (
                <ol className="list-decimal list-inside divide-y divide-gray-200" style={{ counterReset: 'my-counter' }}>
                    {items.map((item: CartItem) => ( // Removed index as key part
                        // Use item.cartItemId which should be unique
                        <li key={item.cartItemId} className="py-4 flex items-center justify-between before:content-[counter(my-counter)] before:mr-4" style={{ counterIncrement: 'my-counter' }}>
                            <div className="flex-grow">
                                <p className="font-medium">
                                    {item.product_name}
                                    <span className="text-xs md:text-xs text-gray-500 pl-2">sku: {item.product_code}</span>
                                    &nbsp;<span className='price-div'>({displayPrice(Number(item.price))})</span> {/* Display final item price */}
                                </p>
                                {item.modifiers && item.modifiers.length > 0 && ( // Check if modifiers exist
                                    <div className='modifiers-div'>
                                        <ul className="list-disc list-inside ">
                                            {item.modifiers.map((modifier) => (
                                                // Use modifier.id for uniqueness within the item
                                                <li key={`${item.cartItemId}-mod-${modifier.id}`} className="text-xs">
                                                    {modifier.product_name}
                                                    <span className="text-xs md:text-xs text-gray-500 pl-2">sku: {modifier.product_code}</span>
                                                    &nbsp;<span className='price-div'>({displayPrice(Number(modifier.product_value))})  </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                <p className="price-div text-sm text-gray-500">
                                    {/* Display calculation based on final item price */}
                                    {displayPrice(item.price)} x {item.quantity} = {displayPrice(item.price * item.quantity)}
                                </p>
                            </div>
                            <div className="ml-4">
                                <button
                                    onClick={() => handleRemoveFromCart(item.cartItemId)}
                                    className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
                                >
                                    Remove
                                </button>
                            </div>
                        </li>
                    ))}
                </ol>
            )}
            {items.length > 0 && (
                <div className="mt-4">
                    <p className="price-div text-lg font-semibold">
                        Total: {displayPrice(calculateTotal())}
                    </p>
                    <button
                        onClick={handleCheckout}
                        // Disable button if customer/user info is missing
                        disabled={effectiveUser.customer_id === undefined || effectiveUser.id === undefined}
                        className={`mt-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded ${(effectiveUser.customer_id === undefined || effectiveUser.id === undefined) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        Checkout
                    </button>
                    {/* Show warning if button is disabled */}
                    {(effectiveUser.customer_id === undefined || effectiveUser.id === undefined) && (
                        <p className="text-red-500 text-sm mt-2">Cannot checkout: Missing customer or user information.</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default Cart;

